import type { ReactNode } from 'react';

import { Box, Button, HStack, Link, Table, Text } from '@chakra-ui/react';

export type DocumentUploadTableRow = {
  id: string;
  createdAt: string;
  filename?: string | null;
};

export function DocumentUploadsTable({
  rows,
  apiHost,
  emptyMessage = 'No files linked to this document yet. Use Upload file… above or paste images into the editor.',
  showFileManagement = false,
  onRename,
  onDelete,
  openColumnHeader,
}: {
  rows: DocumentUploadTableRow[];
  apiHost: string;
  /** Shown when `rows` is empty (e.g. editor block vs. files page). */
  emptyMessage?: string;
  /** Rename/delete actions (document Files page only). */
  showFileManagement?: boolean;
  onRename?: (row: DocumentUploadTableRow) => void;
  onDelete?: (row: DocumentUploadTableRow) => void;
  /** Toolbar for the Open column header (upload / navigation icons). */
  openColumnHeader?: ReactNode;
}) {
  const manageColumnActive = Boolean(
    showFileManagement && onRename && onDelete,
  );
  const columnCount = manageColumnActive ? 4 : 3;

  return (
    <Box overflowX="auto">
      <Table.Root
        size="sm"
        variant="line"
        css={{
          '& thead tr, & tbody tr': {
            backgroundColor: 'var(--input-base-background-color)',
          },
        }}
      >
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>Created</Table.ColumnHeader>
            <Table.ColumnHeader>Filename</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="end">
              {openColumnHeader ?? '\u00a0'}
            </Table.ColumnHeader>
            {manageColumnActive ? (
              <Table.ColumnHeader textAlign="end">Manage</Table.ColumnHeader>
            ) : null}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan={columnCount} textAlign="center" py={6}>
                <Text color="fg.muted" fontSize="sm">
                  {emptyMessage}
                </Text>
              </Table.Cell>
            </Table.Row>
          ) : (
            rows.map((row) => (
              <Table.Row key={row.id}>
                <Table.Cell whiteSpace="nowrap">
                  {new Date(row.createdAt).toLocaleString()}
                </Table.Cell>
                <Table.Cell>{row.filename ?? '—'}</Table.Cell>
                <Table.Cell textAlign="end">
                  <Link
                    href={`${apiHost}/api/upload/${row.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="blue.600"
                  >
                    Open
                  </Link>
                </Table.Cell>
                {manageColumnActive ? (
                  <Table.Cell textAlign="end">
                    <HStack gap={1} justify="flex-end" flexWrap="wrap">
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => {
                          if (!onRename) return;

                          onRename(row);
                        }}
                      >
                        Rename
                      </Button>
                      <Button
                        size="xs"
                        variant="ghost"
                        colorPalette="red"
                        onClick={() => {
                          if (!onDelete) return;

                          onDelete(row);
                        }}
                      >
                        Remove
                      </Button>
                    </HStack>
                  </Table.Cell>
                ) : null}
              </Table.Row>
            ))
          )}
        </Table.Body>
      </Table.Root>
    </Box>
  );
}
